import React from 'react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button/standard-button';
import { ModalBody, ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { PublicBoard } from '@atlassian/jira-boards-common/src/model/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

type Props = {
	board: PublicBoard;
	copyInProgress: boolean;
	onCancel: () => void;
	onCopy: () => void;
};

function CopyBoardsModal(props: Props): React.JSX.Element {
	const { board, copyInProgress, onCancel, onCopy } = props;
	const { formatMessage } = useIntl();
	const onCancelOrNoop = !copyInProgress ? onCancel : noop;

	return (
		<ShortcutScope>
			<ModalDialog
				messageId="copy-boards-modal.modal-dialog"
				messageType="transactional"
				onClose={onCancelOrNoop}
			>
				<ModalHeader hasCloseButton={fg('enghealth-modal-close-button-prevent-close-modal')}>
					<ModalTitle appearance="warning">
						{formatMessage(messages.heading, { boardName: board.name })}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>
						{formatMessage(messages.settingsAndFilterInfoMessage, {
							boardName: board.name,
						})}
					</p>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>{formatMessage(messages.ownerMessage)}</p>
					<ViewTracker />
				</ModalBody>
				<ModalFooter>
					<Button appearance="warning" onClick={onCopy} isDisabled={copyInProgress}>
						{formatMessage(messages.copyButtonCaption)}
					</Button>
					<Button appearance="subtle-link" onClick={onCancelOrNoop} isDisabled={copyInProgress}>
						{formatMessage(messages.cancelButtonCaption)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

CopyBoardsModal.defaultProps = {
	copyInProgress: false,
	onCancel: noop,
	onCopy: noop,
};

export default CopyBoardsModal;
